import { flatObjectToInterface, interfaceDataToFlatObject } from "../common";
import { isMetadata, MetadataNone, metadataStrategyOverwrite, MismatchedMetadataFlavorError, IncorrectConverterError, } from "../metadata";
class BaseUnsafeConverter {
    metadataGenerator;
    constructor(metadataGenerator) {
        this.metadataGenerator = metadataGenerator;
    }
    /**
     * Unpack an object with write metadata
     */
    unpackObjectWithMetadata(withMetadata, metadataFlavor) {
        if (isMetadata(withMetadata) && metadataFlavor === "subcollection") {
            return {
                // Suppress warning for non-flat objects since Firestore data may legitimately contain arrays/maps/sets
                data: flatObjectToInterface(withMetadata.data, { warnOnNonFlatObject: false }),
                metadata: withMetadata.metadata,
            };
        }
        throw new MismatchedMetadataFlavorError(metadataFlavor);
    }
    /**
     * Read an object (without type safety) and fail if we can't read it.
     *
     * @param rawObject Typed as unknown, but practically expected to be a WithMetadata-type variant, because we immediately
     *                  attempt to unpack it
     * @param metadataWriteStrategy The metadata write strategy used for this object
     */
    unpackAndDeserialize(rawObject, metadataWriteStrategy) {
        const { flavor } = metadataWriteStrategy;
        if (flavor === "none") {
            // Suppress warning for non-flat objects since Firestore data may legitimately contain arrays/maps/sets
            return flatObjectToInterface(rawObject, { warnOnNonFlatObject: false });
        }
        const unpacked = this.unpackObjectWithMetadata(rawObject, flavor);
        if (flavor === "subcollection" && isMetadata(unpacked)) {
            // TODO: Convert numeric terminalId to string for pad nodes to fix type inconsistency
            // where terminalId is sometimes stored as number in Firestore but schema expects string.
            // See: https://linear.app/buildwithflux/issue/FLUX-6812
            const data = unpacked.data;
            if (typeof data === "object" && data !== null && "type" in data && "terminalId" in data) {
                if (data.type === "pad" && typeof data.terminalId === "number") {
                    data.terminalId = String(data.terminalId);
                }
            }
            return data;
        }
        throw new MismatchedMetadataFlavorError(flavor);
    }
    /**
     * Pack an object with metadata using a serializer.
     *
     * Packing denotes producing a final object to writing to the database, along with associated metadata. The metadata
     * to be packed can be specified
     *
     * @param modelObject The model to be packed
     * @param metadataWriteStrategy A strategy to use when updating the metadata
     */
    packAndSerialize(modelObject, metadataWriteStrategy) {
        if (metadataWriteStrategy.flavor === "none") {
            return interfaceDataToFlatObject(modelObject);
        }
        let metadata = this.metadataGenerator.generate(metadataWriteStrategy.flavor);
        // Not currently a used path because we only do this on the document, which has its own converter
        if (metadataWriteStrategy.writeStrategy === "specify") {
            metadata = metadataWriteStrategy.metadata;
        }
        if (metadataWriteStrategy.flavor === "subcollection") {
            return {
                data: interfaceDataToFlatObject(modelObject),
                metadata,
            };
        }
        if (metadataWriteStrategy.flavor === "baseDocument") {
            throw new IncorrectConverterError("documents are serialized by a specific converter");
        }
        throw new MismatchedMetadataFlavorError(metadataWriteStrategy.flavor);
    }
}
export class UnsafeAdminConverter extends BaseUnsafeConverter {
    toFirestore(modelObject) {
        return this.packAndSerialize(modelObject, MetadataNone);
    }
    fromFirestore(snapshot) {
        return this.unpackAndDeserialize(snapshot.data(), MetadataNone);
    }
}
export class UnsafeWithSubcollectionOverwriteAdminConverter extends BaseUnsafeConverter {
    toFirestore(modelObject) {
        return this.packAndSerialize(modelObject, metadataStrategyOverwrite("subcollection"));
    }
    fromFirestore(snapshot) {
        return this.unpackAndDeserialize(snapshot.data(), metadataStrategyOverwrite("subcollection"));
    }
}
export class UnsafeClientConverter extends BaseUnsafeConverter {
    toFirestore(modelObject) {
        return this.packAndSerialize(modelObject, MetadataNone);
    }
    fromFirestore(snapshot) {
        return this.unpackAndDeserialize(snapshot.data(), MetadataNone);
    }
}
export class UnsafeWithSubcollectionOverwriteClientConverter extends BaseUnsafeConverter {
    toFirestore(modelObject) {
        return this.packAndSerialize(modelObject, metadataStrategyOverwrite("subcollection"));
    }
    fromFirestore(snapshot) {
        return this.unpackAndDeserialize(snapshot.data(), metadataStrategyOverwrite("subcollection"));
    }
}
/**
 * Admin converter that uses a custom MetadataWriteStrategy for writing.
 * This allows specifying custom metadata (e.g., for managed service writes).
 */
export class UnsafeWithCustomStrategyAdminConverter extends BaseUnsafeConverter {
    writeStrategy;
    constructor(metadataGenerator, writeStrategy) {
        super(metadataGenerator);
        this.writeStrategy = writeStrategy;
    }
    toFirestore(modelObject) {
        return this.packAndSerialize(modelObject, this.writeStrategy);
    }
    fromFirestore(snapshot) {
        return this.unpackAndDeserialize(snapshot.data(), metadataStrategyOverwrite("subcollection"));
    }
}
//# sourceMappingURL=UnsafeConverter.js.map